/* Cart behaviour shared by the cart page (sections/ac-ct-page.liquid) and
   the cart drawer (snippets/popup-cart.liquid). Loaded once from
   layout/theme.liquid.

   - quantity steppers, direct entry and Remove post to /cart/change.js by
     line key (never by row index); the response carries the cart JSON and,
     when the cart page is open, a fresh render of its section through the
     Section Rendering API. The page's [data-ac-ct-content] is swapped for
     that render; the drawer is re-rendered through the theme's own
     controller (theme.Cart.updateCartPopupContent), which fetches
     /cart?view=popup and replaces the inside of .js-popup-cart-ajax. Header
     counters go through theme.Cart.updateCounter, so page, drawer and header
     always show the same confirmed backend cart
   - every mutation runs through one serial queue, quantity clicks are
     debounced per line, and a sequence number drops stale renders
   - the page's order note autosaves to /cart/update.js; an unsaved draft
     survives a re-render and is re-sent. The drawer has no note field and
     its requests never touch the note
   - CHECKOUT (page or drawer) waits for the queue to drain before the real
     form submit goes ahead; the submit stays a native submit of a /cart
     form, which the personalization app listens to
   - the drawer is a modal dialog: focus moves to Close on open, Tab cycles
     inside it, everything outside is inert, the chat bubble is hidden, and
     focus returns to the opener on close. Escape, backdrop and Close are
     the theme's own popup behaviour. The same dialog code serves the
     wishlist drawer (assets/ac-wishlist.js) through window.acDrawerDialog
   - the Cloudlift options app re-processes rows itself after any cart
     request (Edit href, customer preview image, option price labels)

   Listeners are delegated from document, so re-renders and theme-editor
   reloads never double them. */
(function () {
  'use strict';

  var SEL = {
    pageRoot: '[data-ac-ct-root]',
    pageContent: '[data-ac-ct-content]',
    drawerRoot: '.js-popup-cart-ajax',
    drawerClose: '.ac-cd__close',
    scope: '[data-ac-ct-root], .js-popup-cart-ajax',
    live: '[data-ac-ct-live]',
    item: '[data-ac-ct-item]',
    input: '[data-ac-ct-input]',
    step: '[data-ac-ct-step]',
    remove: '[data-ac-ct-remove]',
    error: '[data-ac-ct-error]',
    form: '[data-ac-ct-form]',
    checkout: '[data-ac-ct-checkout]',
    checkoutLabel: '[data-ac-ct-checkout-label]',
    note: '[data-ac-ct-note]',
    noteField: '[data-ac-ct-note-field]',
    noteStatus: '[data-ac-ct-note-status]',
    noteLabel: '[data-ac-ct-note-label]',
    subtotal: '[data-ac-ct-subtotal]',
    pageEmptyTitle: '#ac-ct-empty-title',
    drawerEmptyTitle: '#ac-cd-empty-title'
  };

  var QTY_DEBOUNCE = 400;
  var NOTE_DEBOUNCE = 700;
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), summary, [tabindex]:not([tabindex="-1"])';

  var queue = Promise.resolve();
  var pending = 0;
  var seq = 0;
  var qtyTimers = {};
  var noteTimer = null;
  var noteDraft = null;
  var noteSaving = false;
  var lastError = null;       // { scope: 'page'|'drawer', key, message }
  var allowSubmit = false;
  var liveEl = null;

  document.documentElement.classList.add('ac-ct-js');

  /* ---- scopes ------------------------------------------------------------ */

  function pageRoot() { return document.querySelector(SEL.pageRoot); }
  function drawerRoot() { return document.querySelector(SEL.drawerRoot); }
  function scopeName(el) {
    var s = el && el.closest ? el.closest(SEL.scope) : null;
    if (!s) return null;
    return s.matches(SEL.pageRoot) ? 'page' : 'drawer';
  }
  function scopeEl(name) { return name === 'page' ? pageRoot() : drawerRoot(); }
  function cartUrl() { var r = pageRoot(); return (r && r.getAttribute('data-cart-url')) || (window.theme && window.theme.routes && window.theme.routes.cart_url) || '/cart'; }
  function sectionId() { var r = pageRoot(); return r ? r.getAttribute('data-section-id') : ''; }

  function itemByKey(scope, key) {
    var root = scopeEl(scope);
    if (!root) return null;
    var items = root.querySelectorAll(SEL.item);
    for (var i = 0; i < items.length; i++) if (items[i].getAttribute('data-key') === key) return items[i];
    return null;
  }

  /* ---- helpers ----------------------------------------------------------- */

  function announce(message) {
    var live = document.querySelector(SEL.live);
    if (!live) {
      if (!liveEl) {
        liveEl = document.createElement('div');
        liveEl.className = 'ac-ct-visually-hidden';
        liveEl.setAttribute('aria-live', 'polite');
        liveEl.setAttribute('aria-atomic', 'true');
        document.body.appendChild(liveEl);
      }
      live = liveEl;
    }
    live.textContent = '';
    window.setTimeout(function () { live.textContent = message; }, 60);
  }

  function setBusy(item, busy) {
    if (!item) return;
    item.classList.toggle('is-busy', busy);
    if (busy) item.setAttribute('aria-busy', 'true'); else item.removeAttribute('aria-busy');
  }

  function showError(scope, key, message) {
    lastError = { scope: scope, key: key, message: message };
    var item = itemByKey(scope, key);
    if (!item) return;
    var el = item.querySelector(SEL.error);
    if (!el) return;
    el.textContent = message;
    el.hidden = false;
    el.setAttribute('role', 'alert');
    var input = item.querySelector(SEL.input);
    if (input) input.setAttribute('aria-invalid', 'true');
  }

  function clearError(item) {
    if (!item) return;
    var el = item.querySelector(SEL.error);
    if (el) { el.hidden = true; el.textContent = ''; el.removeAttribute('role'); }
    var input = item.querySelector(SEL.input);
    if (input) input.removeAttribute('aria-invalid');
  }

  function request(url, options) {
    var opts = options || {};
    opts.credentials = 'same-origin';
    return window.fetch(url, opts);
  }

  function enqueue(task) {
    pending++;
    var run = queue.then(function () { return task(); });
    queue = run.then(function () { pending--; }, function () { pending--; });
    return run;
  }

  function whenIdle() {
    return queue.then(function () {
      if (pending > 0 || noteSaving) return whenIdle();
      return null;
    });
  }

  function subtotalText(scope) {
    var root = scopeEl(scope) || document;
    var el = root.querySelector(SEL.subtotal);
    return el ? el.textContent.trim() : '';
  }

  /* ---- focus memory across renders --------------------------------------- */

  function focusInfo() {
    var active = document.activeElement;
    if (!active || !active.closest) return null;
    var scope = scopeName(active);
    if (!scope) return null;
    var item = active.closest(SEL.item);
    if (item) {
      var role = active.matches(SEL.input) ? 'input'
        : active.matches(SEL.remove) ? 'remove'
        : active.matches(SEL.step) ? 'step:' + active.getAttribute('data-ac-ct-step')
        : null;
      return role ? { scope: scope, key: item.getAttribute('data-key'), role: role, nextKey: nextItemKey(item) } : null;
    }
    if (active.matches(SEL.noteField)) return { scope: scope, role: 'note' };
    return { scope: scope, role: 'other' };
  }

  function nextItemKey(item) {
    var next = item.nextElementSibling || item.previousElementSibling;
    return next && next.matches(SEL.item) ? next.getAttribute('data-key') : null;
  }

  function restoreFocus(info, scope) {
    if (!info || info.scope !== scope) return;
    var root = scopeEl(scope);
    if (!root) return;
    var target = null;
    if (info.role === 'note') {
      target = root.querySelector(SEL.noteField);
    } else if (info.role === 'other') {
      target = scope === 'drawer' ? root.querySelector(SEL.drawerClose) : null;
    } else {
      var item = itemByKey(scope, info.key);
      if (item) {
        if (info.role === 'input') target = item.querySelector(SEL.input);
        else if (info.role === 'remove') target = item.querySelector(SEL.remove);
        else if (info.role.indexOf('step:') === 0) {
          target = item.querySelector('[data-ac-ct-step="' + info.role.slice(5) + '"]');
          if (target && target.disabled) target = item.querySelector(SEL.input);
        }
      } else {
        var neighbour = info.nextKey ? itemByKey(scope, info.nextKey) : null;
        target = neighbour ? neighbour.querySelector(SEL.remove)
          : root.querySelector(scope === 'page' ? SEL.pageEmptyTitle : SEL.drawerEmptyTitle)
          || (scope === 'drawer' ? root.querySelector(SEL.drawerClose) : null);
      }
    }
    if (target) { try { target.focus({ preventScroll: true }); } catch (e) { target.focus(); } }
  }

  /* ---- page note draft across renders ------------------------------------ */

  function captureNote() {
    var root = pageRoot();
    var field = root && root.querySelector(SEL.noteField);
    var details = root && root.querySelector(SEL.note);
    if (!field) return null;
    return { value: field.value, open: !!(details && details.open), dirty: noteDraft !== null };
  }

  function restoreNote(saved) {
    if (!saved) return;
    var root = pageRoot();
    var field = root && root.querySelector(SEL.noteField);
    var details = root && root.querySelector(SEL.note);
    if (!field) return;
    if (details && saved.open) details.open = true;
    if (saved.dirty) { field.value = saved.value; scheduleNoteSave(); }
    updateNoteLabel(field.value);
  }

  function updateNoteLabel(value) {
    var label = document.querySelector(SEL.noteLabel);
    if (!label) return;
    var filled = value && value.trim().length > 0;
    label.textContent = label.getAttribute(filled ? 'data-label-filled' : 'data-label-empty') || label.textContent;
  }

  function setNoteStatus(text, state) {
    var el = document.querySelector(SEL.noteStatus);
    if (!el) return;
    el.textContent = text;
    el.classList.toggle('is-error', state === 'error');
    el.classList.toggle('is-saved', state === 'saved');
  }

  /* ---- rendering --------------------------------------------------------- */

  function renderPage(html) {
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var next = doc.querySelector(SEL.pageContent);
    var root = pageRoot();
    var current = root && root.querySelector(SEL.pageContent);
    if (!next || !current) return false;
    var note = captureNote();
    current.parentNode.replaceChild(next, current);
    restoreNote(note);
    return true;
  }

  function refreshPage() {
    if (!pageRoot()) return Promise.resolve();
    return request(cartUrl() + '?sections=' + encodeURIComponent(sectionId()), { headers: { 'Accept': 'application/json' } })
      .then(function (res) { return res.json(); })
      .then(function (data) { var html = data && data[sectionId()]; if (html) renderPage(html); })
      .catch(function () {});
  }

  function refreshDrawer() {
    var t = window.theme;
    if (!drawerRoot() || !t || !t.Cart || typeof t.Cart.updateCartPopupContent !== 'function') return Promise.resolve();
    try { return Promise.resolve(t.Cart.updateCartPopupContent()); } catch (e) { return Promise.resolve(); }
  }

  function syncHeader(cart) {
    var t = window.theme;
    if (t && t.Cart && typeof t.Cart.setCurrentData === 'function') {
      try { t.Cart.setCurrentData(cart); t.Cart.updateCounter(); return; } catch (e) { /* fall through */ }
    }
    var count = cart.item_count;
    var desktop = document.querySelector('[data-js-cart-count-desktop]');
    var mobile = document.querySelector('[data-js-cart-count-mobile]');
    var format = (t && t.strings && t.strings.header && t.strings.header.cart_count_desktop) || 'Bag ({{ count }})';
    if (desktop) { desktop.setAttribute('data-js-cart-count-desktop', count); desktop.textContent = format.replace('{{ count }}', count); }
    if (mobile) { mobile.setAttribute('data-js-cart-count-mobile', count); mobile.textContent = count; }
  }

  function reapplyError() {
    if (!lastError) return;
    var err = lastError; lastError = null;
    showError(err.scope, err.key, err.message);
  }

  /* Apply a confirmed cart to every surface, then restore focus in the
     surface the action came from. */
  function applyCart(cart, sectionHtml, focus) {
    var pageDone = Promise.resolve();
    if (pageRoot()) {
      if (sectionHtml && renderPage(sectionHtml)) { /* rendered */ }
      else pageDone = refreshPage();
    }
    syncHeader(cart);
    var drawerDone = refreshDrawer();
    return Promise.all([pageDone, drawerDone]).then(function () {
      reapplyError();
      restoreFocus(focus, 'page');
      restoreFocus(focus, 'drawer');
    });
  }

  /* ---- cart mutations ---------------------------------------------------- */

  function changeLine(scope, key, quantity, title) {
    var focus = focusInfo();
    var item = itemByKey(scope, key);
    clearError(item);
    setBusy(item, true);
    setBusy(itemByKey(scope === 'page' ? 'drawer' : 'page', key), true);
    var mySeq = ++seq;
    var body = { id: key, quantity: quantity };
    if (pageRoot()) { body.sections = sectionId(); body.sections_url = cartUrl(); }

    return enqueue(function () {
      return request(cartUrl() + '/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify(body)
      }).then(function (res) {
        return res.json().then(function (data) { return { ok: res.ok, data: data }; }, function () { return { ok: false, data: {} }; });
      }).then(function (result) {
        if (mySeq !== seq) return result;          // a newer change will render
        if (result.ok && result.data && result.data.items) {
          var html = result.data.sections ? result.data.sections[sectionId()] : null;
          return applyCart(result.data, html, focus).then(function () {
            if (result.data.item_count === 0) announce('Your bag is empty.');
            else if (quantity === 0) announce((title ? title + ' removed. ' : 'Item removed. ') + 'Subtotal ' + subtotalText(scope) + '.');
            else announce('Quantity updated to ' + quantity + '. Subtotal ' + subtotalText(scope) + '.');
            return result;
          });
        }
        var message = (result.data && (result.data.description || result.data.message)) || 'We could not update your bag. Please try again.';
        lastError = { scope: scope, key: key, message: message };
        announce(message);
        return Promise.all([refreshPage(), refreshDrawer()]).then(function () { reapplyError(); restoreFocus(focus, scope); return result; });
      }).catch(function () {
        showError(scope, key, 'We could not update your bag. Please check your connection and try again.');
        setBusy(itemByKey(scope, key), false);
        setBusy(itemByKey(scope === 'page' ? 'drawer' : 'page', key), false);
      });
    });
  }

  function scheduleQuantity(scope, item) {
    var key = item.getAttribute('data-key');
    var title = item.getAttribute('data-title') || '';
    var id = scope + ':' + key;
    window.clearTimeout(qtyTimers[id]);
    qtyTimers[id] = window.setTimeout(function () {
      delete qtyTimers[id];
      var current = itemByKey(scope, key);
      var input = current && current.querySelector(SEL.input);
      if (!input) return;
      var value = parseInt(input.value, 10);
      var last = parseInt(input.getAttribute('data-ac-ct-last'), 10);
      if (isNaN(value) || value === last) return;
      changeLine(scope, key, value, title);
    }, QTY_DEBOUNCE);
  }

  function flushTimers() {
    Object.keys(qtyTimers).forEach(function (id) {
      window.clearTimeout(qtyTimers[id]);
      delete qtyTimers[id];
      var scope = id.split(':')[0];
      var key = id.slice(scope.length + 1);
      var item = itemByKey(scope, key);
      var input = item && item.querySelector(SEL.input);
      if (!input) return;
      var value = parseInt(input.value, 10);
      var last = parseInt(input.getAttribute('data-ac-ct-last'), 10);
      if (!isNaN(value) && value !== last) changeLine(scope, key, value, item.getAttribute('data-title') || '');
    });
    if (noteTimer) { window.clearTimeout(noteTimer); noteTimer = null; saveNote(); }
  }

  function validateInput(scope, input) {
    var item = input.closest(SEL.item);
    var key = item.getAttribute('data-key');
    var value = parseInt(input.value, 10);
    var min = parseInt(input.getAttribute('min'), 10) || 1;
    var max = parseInt(input.getAttribute('max'), 10);
    if (isNaN(value) || value < min) {
      showError(scope, key, 'Enter a quantity of at least ' + min + ', or use Remove to take this item out of your bag.');
      input.value = input.getAttribute('data-ac-ct-last');
      return false;
    }
    if (!isNaN(max) && value > max) {
      showError(scope, key, 'Only ' + max + ' of this item can be added.');
      input.value = String(max);
      return true;
    }
    clearError(item);
    return true;
  }

  function syncStepButtons(item) {
    var input = item.querySelector(SEL.input);
    var minus = item.querySelector('[data-ac-ct-step="-1"]');
    if (!input || !minus) return;
    minus.disabled = (parseInt(input.value, 10) || 1) <= 1;
  }

  /* ---- order note (page only) ------------------------------------------- */

  function scheduleNoteSave() {
    var field = document.querySelector(SEL.noteField);
    if (!field) return;
    noteDraft = field.value;
    setNoteStatus('Unsaved changes', '');
    window.clearTimeout(noteTimer);
    noteTimer = window.setTimeout(function () { noteTimer = null; saveNote(); }, NOTE_DEBOUNCE);
  }

  function saveNote() {
    var field = document.querySelector(SEL.noteField);
    if (!field || noteDraft === null) return Promise.resolve();
    var value = noteDraft;
    noteSaving = true;
    setNoteStatus('Saving…', '');
    return enqueue(function () {
      return request(cartUrl() + '/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ note: value })
      }).then(function (res) {
        if (!res.ok) throw new Error('note');
        return res.json();
      }).then(function (cart) {
        if (noteDraft === value) noteDraft = null;
        noteSaving = false;
        setNoteStatus(value.trim() ? 'Note saved' : 'Note removed', 'saved');
        updateNoteLabel(value);
        if (noteDraft !== null) scheduleNoteSave();
        if (window.theme && window.theme.Cart && typeof window.theme.Cart.setCurrentData === 'function') {
          try { window.theme.Cart.setCurrentData(cart); } catch (e) { /* ignore */ }
        }
      }).catch(function () {
        noteSaving = false;
        setNoteStatus('We could not save your note. It will be sent with your order when you check out.', 'error');
      });
    });
  }

  /* ---- checkout guard ---------------------------------------------------- */

  function guardedSubmit(form, event) {
    var button = form.querySelector(SEL.checkout);
    var label = button ? button.querySelector(SEL.checkoutLabel) : null;
    var original = label ? label.textContent : '';
    var hasTimers = Object.keys(qtyTimers).length > 0 || !!noteTimer;
    if (pending === 0 && !noteSaving && !hasTimers && noteDraft === null) return;
    event.preventDefault();
    flushTimers();
    if (button) { button.classList.add('is-waiting'); button.setAttribute('aria-disabled', 'true'); }
    if (label) label.textContent = 'Saving…';
    announce('Saving your changes before checkout.');
    var scope = scopeName(form) || 'page';
    whenIdle().then(function () {
      var liveForm = null;
      var root = scopeEl(scope);
      if (root) liveForm = root.querySelector(SEL.form);
      var liveButton = liveForm && liveForm.querySelector(SEL.checkout);
      var liveLabel = liveButton && liveButton.querySelector(SEL.checkoutLabel);
      if (liveButton) { liveButton.classList.remove('is-waiting'); liveButton.removeAttribute('aria-disabled'); }
      if (liveLabel) liveLabel.textContent = original;
      var openError = root && root.querySelector(SEL.error + ':not([hidden])');
      if (lastError || openError) {
        announce('Please review the highlighted item before checking out.');
        if (openError) { openError.setAttribute('tabindex', '-1'); openError.focus(); }
        return;
      }
      if (!liveForm) return;
      if (liveButton && liveButton.disabled) return;   // a line is missing its personalization (2026-10-02)
      allowSubmit = true;
      if (typeof liveForm.requestSubmit === 'function' && liveButton) liveForm.requestSubmit(liveButton);
      else {
        var hidden = document.createElement('input');
        hidden.type = 'hidden'; hidden.name = 'checkout'; hidden.value = '1';
        liveForm.appendChild(hidden);
        liveForm.submit();
      }
    });
  }

  /* ---- drawer dialog behaviour ------------------------------------------ */
  /* Shared by every drawer built on the theme's popup frame. The cart
     registers itself below; assets/ac-wishlist.js registers the wishlist
     drawer and the full-wishlist popup through window.acDrawerDialog. One
     dialog is active at a time: focus moves to its Close on open, Tab cycles
     inside it, everything outside is inert, body.ac-cd-open hides the chat
     bubble, and focus returns to the opener on close. When the theme swaps
     one popup for another without close events (VIEW WISHLIST opens
     wishlist-full over the wishlist drawer) the newcomer takes over and the
     original opener keeps the focus return. */

  var dialogs = {};        // popup name -> { root, close, bound }
  var activeDialog = null;
  var opener = null;
  var inertEls = [];

  function activeRoot() {
    var d = activeDialog && dialogs[activeDialog];
    return d ? document.querySelector(d.root) : null;
  }

  function setInert(on) {
    if (on) {
      inertEls = [];
      var kids = document.body.children;
      for (var i = 0; i < kids.length; i++) {
        var el = kids[i];
        if (el.matches('script, style, link') || el.classList.contains('js-popup') || el.hasAttribute('inert')) continue;
        el.setAttribute('inert', '');
        inertEls.push(el);
      }
    } else {
      inertEls.forEach(function (el) { el.removeAttribute('inert'); });
      inertEls = [];
    }
  }

  function dialogFocusables() {
    var root = activeRoot();
    if (!root) return [];
    return Array.prototype.filter.call(root.querySelectorAll(FOCUSABLE), function (el) {
      return el.offsetParent !== null || el === document.activeElement;
    });
  }

  function onDialogCallBefore(name, $popup, $target) {
    var takeover = !!(activeDialog && activeDialog !== name);
    if (takeover) {
      setInert(false);                     // the theme hides the previous popup without close events
    } else {
      opener = ($target && $target[0]) || (document.activeElement !== document.body ? document.activeElement : null);
    }
    activeDialog = name;
    var d = dialogs[name];
    if (d) d.opened = false;
    if (takeover && d) {
      /* Popup over popup: the backdrop is already visible, so the theme's
         call.after (which waits for the backdrop transition) never fires.
         Watch for the newcomer to become visible and finish the open here. */
      (function watch(tries) {
        if (activeDialog !== name || d.opened) return;
        var body = document.querySelector('[data-js-popup-name="' + name + '"]');
        if (body && body.classList.contains('visible')) { onDialogCallAfter(name); return; }
        if (tries < 80) window.setTimeout(function () { watch(tries + 1); }, 50);
      })(0);
    }
  }
  function onDialogCallAfter(name) {
    /* A popup opened on page load (the form confirmations) can fire its
       call.before before this registry has bound its handlers; adopt it
       here rather than leave it without dialog semantics. */
    if (!activeDialog) activeDialog = name;
    if (activeDialog !== name) return;
    var dd = dialogs[name];
    if (dd) { if (dd.opened) return; dd.opened = true; }
    document.body.classList.add('ac-cd-open');
    setInert(true);
    var d = dialogs[name];
    var root = activeRoot();
    var close = root && d.close ? root.querySelector(d.close) : null;
    var target = close && close.tabIndex >= 0 ? close : root;
    if (!target) return;
    if (target === root && !root.hasAttribute('tabindex')) root.setAttribute('tabindex', '-1');
    try { target.focus({ preventScroll: true }); } catch (e) { target.focus(); }
  }
  function onDialogCloseBefore(name) {
    if (dialogs[name]) dialogs[name].opened = false;
    if (activeDialog !== name) return;
    activeDialog = null;
    document.body.classList.remove('ac-cd-open');
    setInert(false);
  }
  function onDialogCloseAfter(name) {
    if (activeDialog) return;              // another dialog took over; it returns focus when it closes
    var target = opener && document.contains(opener) && typeof opener.focus === 'function' ? opener : null;
    var d = dialogs[name];
    if (!target && d && typeof d.returnTo === 'function') {
      /* No opener (the dialog opened on page load, after a form post): the
         registrant names somewhere sensible, such as the form that posted. */
      target = d.returnTo();
      if (target && !target.hasAttribute('tabindex') && !target.matches(FOCUSABLE)) target.setAttribute('tabindex', '-1');
    }
    if (target) { try { target.focus({ preventScroll: true }); } catch (e) { target.focus(); } }
    opener = null;
  }

  function bindDialog(name) {
    var d = dialogs[name];
    if (!d) return false;
    if (d.bound) return true;
    var t = window.theme;
    if (!t || !t.Popups || typeof t.Popups.addHandler !== 'function') return false;
    t.Popups.addHandler(name, 'call.before', function ($popup, $target) { onDialogCallBefore(name, $popup, $target); });
    t.Popups.addHandler(name, 'call.after', function () { onDialogCallAfter(name); });
    t.Popups.addHandler(name, 'close.before', function () { onDialogCloseBefore(name); });
    t.Popups.addHandler(name, 'close.after', function () { onDialogCloseAfter(name); });
    d.bound = true;
    return true;
  }

  function registerDialog(name, opts) {
    if (!name || !opts || !opts.root) return;
    if (dialogs[name] && dialogs[name].bound) return;
    dialogs[name] = { root: opts.root, close: opts.close || null, returnTo: opts.returnTo || null, bound: false };
    bindDialog(name);
  }

  registerDialog('cart', { root: SEL.drawerRoot, close: SEL.drawerClose });
  window.acDrawerDialog = { register: registerDialog };

  (function waitForTheme(tries) {
    var pending = Object.keys(dialogs).filter(function (name) { return !bindDialog(name); });
    if (!pending.length || tries > 100) return;
    window.setTimeout(function () { waitForTheme(tries + 1); }, 100);
  })(0);

  document.addEventListener('keydown', function (event) {
    if (!activeDialog || event.key !== 'Tab') return;
    var list = dialogFocusables();
    if (!list.length) return;
    var first = list[0], last = list[list.length - 1];
    var root = activeRoot();
    var inside = root && root.contains(document.activeElement);
    if (event.shiftKey) {
      if (!inside || document.activeElement === first) { event.preventDefault(); last.focus(); }
    } else if (!inside || document.activeElement === last) { event.preventDefault(); first.focus(); }
  });

  /* ---- events (delegated) ------------------------------------------------ */

  document.addEventListener('click', function (event) {
    var target = event.target;
    if (!target.closest) return;

    var step = target.closest(SEL.step);
    if (step && scopeName(step)) {
      event.preventDefault();
      var scope = scopeName(step);
      var item = step.closest(SEL.item);
      var input = item.querySelector(SEL.input);
      if (!input || step.disabled) return;
      var min = parseInt(input.getAttribute('min'), 10) || 1;
      var max = parseInt(input.getAttribute('max'), 10);
      var value = (parseInt(input.value, 10) || min) + parseInt(step.getAttribute('data-ac-ct-step'), 10);
      if (value < min) value = min;
      if (!isNaN(max) && value > max) { value = max; showError(scope, item.getAttribute('data-key'), 'Only ' + max + ' of this item can be added.'); }
      else clearError(item);
      input.value = String(value);
      syncStepButtons(item);
      scheduleQuantity(scope, item);
      return;
    }

    var remove = target.closest(SEL.remove);
    if (remove && scopeName(remove)) {
      event.preventDefault();
      var rScope = scopeName(remove);
      var rItem = remove.closest(SEL.item);
      var key = rItem.getAttribute('data-key');
      window.clearTimeout(qtyTimers[rScope + ':' + key]);
      delete qtyTimers[rScope + ':' + key];
      changeLine(rScope, key, 0, rItem.getAttribute('data-title') || '');
    }
  });

  document.addEventListener('change', function (event) {
    var input = event.target;
    if (!input.matches || !input.matches(SEL.input)) return;
    var scope = scopeName(input);
    if (!scope) return;
    var item = input.closest(SEL.item);
    if (!validateInput(scope, input)) { syncStepButtons(item); return; }
    syncStepButtons(item);
    scheduleQuantity(scope, item);
  });

  document.addEventListener('keydown', function (event) {
    var input = event.target;
    if (!input.matches || !input.matches(SEL.input) || event.key !== 'Enter') return;
    event.preventDefault();                  // Enter confirms the quantity; it must not submit to checkout
    var scope = scopeName(input);
    if (!scope) return;
    var item = input.closest(SEL.item);
    if (validateInput(scope, input)) { syncStepButtons(item); scheduleQuantity(scope, item); }
  });

  document.addEventListener('input', function (event) {
    var field = event.target;
    if (field.matches && field.matches(SEL.noteField)) scheduleNoteSave();
  });

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form.matches || !form.matches(SEL.form)) return;
    var submitter = event.submitter;
    if (submitter && submitter.name === 'update') return;   // no-JS fallback button, plain post
    if (allowSubmit) { allowSubmit = false; return; }
    guardedSubmit(form, event);
  });

  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form.matches || !form.matches(SEL.form) || event.defaultPrevented) return;
    var button = form.querySelector(SEL.checkout);
    if (button) {
      window.setTimeout(function () { button.disabled = true; }, 0);
      window.setTimeout(function () { button.disabled = false; }, 5000);   // an app may cancel the navigation
    }
  });
})();
